import type { Protocol, MessageHandler, SubscriberId, Version } from "./index";
import type * as Types from "../../../domain";
import type { GridSnapshot } from "../../types";

// --- Constants ---
const ENCODING_UTF8 = new TextEncoder();
const DECODING_UTF8 = new TextDecoder();

const TAGS = {
  UPDATE: 1,
  SNAPSHOT_REQ: 2,
  SNAPSHOT_RES: 3,
} as const;

const UPDATE_TYPES = {
  ROOT: 1,
  CALCULATED: 2,
} as const;

const ROW_TYPE = {
  OFFSET: 0,
  LABEL: 1,
} as const;

const RESULT_TYPE = {
  OK: 0,
  ERR: 1,
} as const;

export class BinaryProtocol implements Protocol {
  // Reuse buffer to reduce GC pressure
  private writer = new BinaryWriter(1024 * 1024); // Start at 1MB

  encodeUpdate(from: SubscriberId, version: Version, event: Types.GridUpdateEvent): Uint8Array {
    this.writer.reset();

    // Header: [TAG][FROM][VER]
    this.writer.writeUint8(TAGS.UPDATE);
    this.writer.writeString(from);
    this.writer.writeUint32(version);

    if (event.type === "grid:update:root") {
      this.encodeRootUpdate(event.data);
    } else {
      this.encodeCalculatedUpdate(event.data);
    }

    return this.writer.getBuffer();
  }

  encodeSnapshotRequest(from: SubscriberId, to?: SubscriberId): Uint8Array {
    this.writer.reset();
    this.writer.writeUint8(TAGS.SNAPSHOT_REQ);
    this.writer.writeString(from);
    this.writer.writeUint8(to ? 1 : 0); // Has Target Flag
    if (to) this.writer.writeString(to);
    return this.writer.getBuffer();
  }

  encodeSnapshotResponse(from: SubscriberId, to: SubscriberId, version: Version, snapshot: GridSnapshot): Uint8Array {
    this.writer.reset();
    this.writer.writeUint8(TAGS.SNAPSHOT_RES);
    this.writer.writeString(from);
    this.writer.writeString(to);
    this.writer.writeUint32(version);

    // Sequence: [ROOT_TAG + ROOT_DATA] then [CALC_TAG + CALC_DATA]
    this.encodeRootUpdate({ update: snapshot.root, remove: [] });
    this.encodeCalculatedUpdate(snapshot.calculated);

    return this.writer.getBuffer();
  }

  decode(buffer: Uint8Array, handler: MessageHandler): void {
    const reader = new BinaryReader(buffer);
    const tag = reader.readUint8();

    switch (tag) {
      case TAGS.UPDATE: {
        const from = reader.readString() as SubscriberId;
        const version = reader.readUint32() as Version;
        const updateType = reader.readUint8();

        if (updateType === UPDATE_TYPES.ROOT) {
          handler.onUpdate(from, version, {
            type: "grid:update:root",
            data: this.decodeRootUpdate(reader),
          });
        } else if (updateType === UPDATE_TYPES.CALCULATED) {
          handler.onUpdate(from, version, {
            type: "grid:update:calculated",
            data: this.decodeCalculatedUpdate(reader),
          });
        }
        break;
      }
      case TAGS.SNAPSHOT_REQ: {
        const from = reader.readString() as SubscriberId;
        const hasTo = reader.readUint8() === 1;
        const to = hasTo ? (reader.readString() as SubscriberId) : undefined;
        handler.onSnapshotRequest(from, to);
        break;
      }
      case TAGS.SNAPSHOT_RES: {
        const from = reader.readString() as SubscriberId;
        const to = reader.readString() as SubscriberId;
        const version = reader.readUint32() as Version;

        // 1. Validate and Decode Root
        const rootTag = reader.readUint8();
        if (rootTag !== UPDATE_TYPES.ROOT) {
          throw new Error(`Snapshot Corruption: Expected Root Tag (1), got ${rootTag}`);
        }
        const rootData = this.decodeRootUpdate(reader);

        // 2. Validate and Decode Calculated
        const calcTag = reader.readUint8();
        if (calcTag !== UPDATE_TYPES.CALCULATED) {
          throw new Error(`Snapshot Corruption: Expected Calc Tag (2), got ${calcTag}`);
        }
        const calcData = this.decodeCalculatedUpdate(reader);

        handler.onSnapshotResponse(from, to, version, {
          root: rootData.update,
          calculated: calcData,
        });
        break;
      }
    }
  }

  // --- Sub-Encoders ---

  private encodeRootUpdate(data: { update: Types.RootProduct[]; remove: Types.ProductId[] }) {
    this.writer.writeUint8(UPDATE_TYPES.ROOT);

    // 1. Build Local Dictionaries (Dedup strings in this batch)
    const prodDict = new Dictionary();
    const fieldDict = new Dictionary();

    for (const u of data.update) {
      prodDict.add(u.product);
      fieldDict.add(u.field);
    }
    for (const r of data.remove) {
      prodDict.add(r);
    }

    // 2. Write Dictionaries
    prodDict.writeTo(this.writer);
    fieldDict.writeTo(this.writer);

    // 3. Write Removes
    this.writer.writeUint32(data.remove.length);
    for (const r of data.remove) {
      this.writer.writeUint16(prodDict.get(r));
    }

    // 4. Write Updates
    this.writer.writeUint32(data.update.length);
    for (const u of data.update) {
      this.writer.writeUint16(prodDict.get(u.product));
      this.writer.writeUint16(fieldDict.get(u.field));
      this.writer.writeUint32(u.offset);
      // Handle nulls as NaN for compact wire format
      const val = u.result === null ? NaN : u.result;
      this.writer.writeFloat64(val);
    }
  }

  private encodeCalculatedUpdate(data: Types.CalculatedProduct[]) {
    this.writer.writeUint8(UPDATE_TYPES.CALCULATED);

    const prodDict = new Dictionary();
    const fieldDict = new Dictionary();
    const labelDict = new Dictionary();

    for (const u of data) {
      prodDict.add(u.product);
      fieldDict.add(u.field);
      if (typeof u.row === "string") labelDict.add(u.row);
    }

    prodDict.writeTo(this.writer);
    fieldDict.writeTo(this.writer);
    labelDict.writeTo(this.writer);

    this.writer.writeUint32(data.length);
    for (const u of data) {
      this.writer.writeUint16(prodDict.get(u.product));
      this.writer.writeUint16(fieldDict.get(u.field));

      // Row (Union: Offset | Label)
      if (typeof u.row === "number") {
        this.writer.writeUint8(ROW_TYPE.OFFSET);
        this.writer.writeUint32(u.row);
      } else {
        this.writer.writeUint8(ROW_TYPE.LABEL);
        this.writer.writeUint16(labelDict.get(u.row));
      }

      // Result (Union: Ok | Err)
      if (u.result.Err !== undefined) {
        this.writer.writeUint8(RESULT_TYPE.ERR);
        this.writer.writeString(u.result.Err);
      } else {
        this.writer.writeUint8(RESULT_TYPE.OK);
        this.writer.writeFloat64(u.result.Ok ?? NaN);
      }
    }
  }

  // --- Sub-Decoders ---

  private decodeRootUpdate(reader: BinaryReader) {
    const prods = reader.readStringArray();
    const fields = reader.readStringArray();

    const removeCount = reader.readUint32();
    const remove: Types.ProductId[] = [];
    for (let i = 0; i < removeCount; i++) {
      remove.push(prods[reader.readUint16()] as Types.ProductId);
    }

    const updateCount = reader.readUint32();
    const update: Types.RootProduct[] = new Array(updateCount);
    for (let i = 0; i < updateCount; i++) {
      const pId = reader.readUint16();
      const fId = reader.readUint16();
      const offset = reader.readUint32();
      const val = reader.readFloat64();

      update[i] = {
        product: prods[pId] as Types.ProductId,
        field: fields[fId] as Types.Field,
        offset: offset as Types.Offset,
        result: Number.isNaN(val) ? null : val,
      };
    }
    return { update, remove };
  }

  private decodeCalculatedUpdate(reader: BinaryReader) {
    const prods = reader.readStringArray();
    const fields = reader.readStringArray();
    const labels = reader.readStringArray();

    const count = reader.readUint32();
    const result: Types.CalculatedProduct[] = new Array(count);

    for (let i = 0; i < count; i++) {
      const pId = reader.readUint16();
      const fId = reader.readUint16();

      // Row
      let row: Types.Offset | Types.Label;
      if (reader.readUint8() === ROW_TYPE.OFFSET) {
        row = reader.readUint32() as Types.Offset;
      } else {
        row = labels[reader.readUint16()] as Types.Label;
      }

      // Result
      let calcRes: Types.CalcResult;
      if (reader.readUint8() === RESULT_TYPE.ERR) {
        calcRes = { Err: reader.readString(), Ok: undefined };
      } else {
        calcRes = { Ok: reader.readFloat64(), Err: undefined };
      }

      result[i] = {
        product: prods[pId] as Types.ProductId,
        field: fields[fId] as Types.Field,
        row,
        result: calcRes,
      };
    }
    return result;
  }
}

// ============================================================================
// HELPERS (Writer/Reader/Dictionary)
// ============================================================================

class Dictionary {
  private map = new Map<string, number>();
  private list: string[] = [];

  add(str: string) {
    if (!this.map.has(str)) {
      this.map.set(str, this.list.length);
      this.list.push(str);
    }
  }

  get(str: string): number {
    return this.map.get(str)!;
  }

  writeTo(writer: BinaryWriter) {
    writer.writeUint16(this.list.length);
    for (const s of this.list) {
      writer.writeString(s);
    }
  }
}

class BinaryWriter {
  private buffer: Uint8Array;
  private view: DataView;
  private offset = 0;

  constructor(size: number) {
    this.buffer = new Uint8Array(size);
    this.view = new DataView(this.buffer.buffer);
  }

  reset() {
    this.offset = 0;
  }

  getBuffer() {
    return this.buffer.subarray(0, this.offset);
  }

  private ensure(bytes: number) {
    if (this.offset + bytes > this.buffer.byteLength) {
      const newSize = Math.max(this.buffer.byteLength * 2, this.offset + bytes);
      const newBuff = new Uint8Array(newSize);
      newBuff.set(this.buffer);
      this.buffer = newBuff;
      this.view = new DataView(this.buffer.buffer);
    }
  }

  writeUint8(v: number) {
    this.ensure(1);
    this.view.setUint8(this.offset, v);
    this.offset += 1;
  }
  writeUint16(v: number) {
    this.ensure(2);
    this.view.setUint16(this.offset, v);
    this.offset += 2;
  }
  writeUint32(v: number) {
    this.ensure(4);
    this.view.setUint32(this.offset, v);
    this.offset += 4;
  }
  writeFloat64(v: number) {
    this.ensure(8);
    this.view.setFloat64(this.offset, v);
    this.offset += 8;
  }
  writeString(str: string) {
    this.ensure(2 + str.length * 3);
    const result = ENCODING_UTF8.encodeInto(str, this.buffer.subarray(this.offset + 2));
    this.view.setUint16(this.offset, result.written);
    this.offset += 2 + result.written;
  }
}

class BinaryReader {
  private view: DataView;
  private offset = 0;
  private buffer: Uint8Array;

  constructor(buffer: Uint8Array) {
    this.buffer = buffer;
    this.view = new DataView(buffer.buffer, buffer.byteOffset, buffer.byteLength);
  }

  readUint8() {
    const v = this.view.getUint8(this.offset);
    this.offset += 1;
    return v;
  }
  readUint16() {
    const v = this.view.getUint16(this.offset);
    this.offset += 2;
    return v;
  }
  readUint32() {
    const v = this.view.getUint32(this.offset);
    this.offset += 4;
    return v;
  }
  readFloat64() {
    const v = this.view.getFloat64(this.offset);
    this.offset += 8;
    return v;
  }

  readString() {
    const len = this.readUint16();
    const bytes = this.buffer.subarray(this.offset, this.offset + len);
    this.offset += len;
    return DECODING_UTF8.decode(bytes);
  }

  readStringArray() {
    const len = this.readUint16();
    const arr = new Array(len);
    for (let i = 0; i < len; i++) arr[i] = this.readString();
    return arr;
  }
}
